import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import { getCategoryLimit } from '@common/utils/widgetConfig';
import { axisTooltipFormatter } from '@common/utils/chartTooltipFormatter';
import { truncateLabel, MAX_CHART_LABEL_LENGTH } from '@common/utils/chartUtils';
import ChartWidgetContainer from '@common/components/ChartWidgetContainer';

function StackedBarChartWidget({ widget }) {
  const { t } = useTranslation();
  const [labelTooltip, setLabelTooltip] = useState(null);
  const { data, config } = widget;

  const otherLabel = t('dashboard.widgets.other');

  const onAxisLabelOver = useCallback((params) => {
    if (params.componentType === 'xAxis' && String(params.value).length > MAX_CHART_LABEL_LENGTH) {
      setLabelTooltip({ text: params.value, x: params.event.offsetX, y: params.event.offsetY });
    }
  }, []);

  const onAxisLabelOut = useCallback((params) => {
    if (params.componentType === 'xAxis') {
      setLabelTooltip(null);
    }
  }, []);

  const chartEvents = useMemo(() => ({
    mouseover: onAxisLabelOver,
    mouseout: onAxisLabelOut,
  }), [onAxisLabelOver, onAxisLabelOut]);

  const option = useMemo(() => {
    if (!data || !data.series || !Array.isArray(data.series) || data.series.length === 0) return null;

    const CATEGORY_LIMIT = getCategoryLimit(config);
    let processedCategories = data.categories || [];
    let processedSeries = data.series || [];

    if (CATEGORY_LIMIT && processedSeries.length > CATEGORY_LIMIT) {
      const topSeries = processedSeries.slice(0, CATEGORY_LIMIT);
      const remainingSeries = processedSeries.slice(CATEGORY_LIMIT);
      const otherData = processedCategories.map((_, categoryIndex) =>
        remainingSeries.reduce((sum, series) => sum + (series.data[categoryIndex] || 0), 0),
      );
      const hasOtherValues = otherData.some((v) => v > 0);

      processedSeries = hasOtherValues
        ? [...topSeries, { name: otherLabel, data: otherData }]
        : topSeries;
    }

    if (CATEGORY_LIMIT && processedCategories.length > CATEGORY_LIMIT) {
      const topCategories = processedCategories.slice(0, CATEGORY_LIMIT);

      const withSlices = processedSeries.map((series) => {
        const topData = series.data.slice(0, CATEGORY_LIMIT);
        const remainingData = series.data.slice(CATEGORY_LIMIT);
        const otherValue = remainingData.reduce((sum, val) => sum + val, 0);
        return { series, topData, otherValue };
      });

      const hasAnyOther = withSlices.some((s) => s.otherValue > 0);

      processedSeries = withSlices.map(({ series, topData, otherValue }) => ({
        ...series,
        data: hasAnyOther ? [...topData, otherValue] : topData,
      }));

      processedCategories = hasAnyOther ? [...topCategories, otherLabel] : topCategories;
    }

    const hasLongLabels = processedCategories && processedCategories.some((cat) => String(cat).length > 10);

    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        appendToBody: true,
        axisPointer: {
          type: 'shadow',
        },
        formatter: axisTooltipFormatter,
      },
      legend: {
        data: processedSeries.map((s) => s.name),
        textStyle: {
          fontSize: 11,
        },
        formatter: (name) => truncateLabel(name),
        tooltip: {
          show: true,
          formatter: (params) => params.name,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: hasLongLabels ? '12%' : '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: processedCategories,
        axisLabel: {
          fontSize: 11,
          rotate: hasLongLabels ? 45 : 0,
          triggerEvent: true,
          formatter: (value) => truncateLabel(value),
        },
        tooltip: {
          show: true,
          formatter: (params) => params.value,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 11,
        },
      },
      series: processedSeries.map((series, index) => ({
        name: series.name,
        type: 'bar',
        stack: 'total',
        data: series.data,
        itemStyle: {
          color: series.color || `hsl(${index * 60}, 70%, 50%)`,
        },
      })),
    };
  }, [data, config, otherLabel]);

  return (
    <ChartWidgetContainer widget={widget} option={option}>
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <ReactECharts
          option={option}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          onEvents={chartEvents}
        />
        {labelTooltip && (
          <div
            style={{
              position: 'absolute',
              left: labelTooltip.x,
              top: Math.max(0, labelTooltip.y - 30),
              background: 'rgba(0,0,0,0.8)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 12,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}
          >
            {labelTooltip.text}
          </div>
        )}
      </div>
    </ChartWidgetContainer>
  );
}

export default StackedBarChartWidget;
